import * as React from 'react';
import { useEffect, useRef, useState, useCallback } from 'react';
import styles from '../../styles/flyouts/stages/StagesFlyout.scss';
import { connect } from 'react-redux';
import { withRouter } from 'react-router';
import CloseBigIcon from '../../assets/images/close-big-15x15.svg';
import classNames from 'classnames';
import Properties from './Properties';
import * as _ from 'lodash';
import { __, setTableParams } from '../../core/utils';
import Tooltip from '../../components/Tooltip';
import ArrowDownMiddleIcon from '../../assets/images/arrow-down-middle-15x15.svg';
import { Element, Events, scrollSpy } from 'react-scroll';
import {
  listProjectStages,
  readStage,
  updateStageOrder,
} from '../../redux/actions/table/stages';
import { setNotification } from '../../redux/actions/general/notification';
import { listActiveModules } from '../../redux/actions/general/active_modules';
import MoveRowUpIcon from '../../assets/images/move-row-up-15x15.svg';
import MoveRowDownIcon from '../../assets/images/move-row-down-15x15.svg';
import Substages from './Substages';

const mapStateToProps = (state) => {
  return {
    tab_opened: _.get(state.table, ['stages', 'tab']),
    clicked_row: _.get(state.table, ['stages', 'clicked_row']),
  };
};

const mapDispatchToProps = (dispatch) => {
  return {
    actions: {
      setTableParams: (params) =>
        dispatch({
          type: 'table.stages/SET_TABLE_PARAMS',
          params,
        }),
    },
  };
};

const StagesFlyout = ({
  data,
  readAction,
  is_project_administrator,
  tab_opened,
  actions,
  match,
}) => {
  const [collapsedTabs, setCollapsedTabs] = useState([]);
  const [highlightedTab, setHighlightedTab] = useState(null);
  const [activeTab, setActiveTab] = useState('properties');
  const highlightTimeout = useRef(null);
  const scrollRef = useRef(null);
  const tabs = ['properties', 'substages'];

  // Initialize scrollSpy and handle scroll events
  useEffect(() => {
    scrollSpy.update();

    // Register scroll event handler
    Events.scrollEvent.register('begin', (to) => {
      setActiveTab(to);
    });

    // Select first tab by default
    if (!tab_opened) {
      setActiveTab('properties');
    }

    return () => {
      Events.scrollEvent.remove('begin');
      if (highlightTimeout.current) {
        clearTimeout(highlightTimeout.current);
      }
    };
  }, []);

  // Handle tab_opened prop changes
  useEffect(() => {
    if (tab_opened) {
      setActiveTab(tab_opened);
      setTableParams('stages', {
        tab: undefined,
      });
    }
  }, [tab_opened]);

  // Handle data changes
  useEffect(() => {
    if (data?.id) {
      setCollapsedTabs([]);
    }
  }, [data?.id]);

  const toggleCollapseTab = useCallback((tab) => {
    setCollapsedTabs((prev) => _.xor(prev, [tab]));
  }, []);

  const highlightSection = useCallback((section) => {
    setHighlightedTab(section);
    setActiveTab(section);

    if (highlightTimeout.current) {
      clearTimeout(highlightTimeout.current);
    }
    highlightTimeout.current = setTimeout(() => {
      setHighlightedTab(null);
    }, 200);
  }, []);

  const toggleCollapseTabs = useCallback(() => {
    if (_.size(collapsedTabs) === _.size(tabs)) {
      setCollapsedTabs([]);
    } else {
      setCollapsedTabs(tabs);
    }
    if (scrollRef.current) {
      scrollRef.current.scrollTop = 0;
    }
  }, [collapsedTabs, tabs]);

  const handleTabClick = useCallback(
    (tab, e) => {
      e.preventDefault();
      setCollapsedTabs((prev) => _.reject(prev, (section) => section === tab));
      highlightSection(tab);
      setActiveTab(tab);

      if (scrollRef.current) {
        if (tab === 'properties') {
          // Scroll to top for properties tab
          scrollRef.current.scrollTo({
            top: 0,
            behavior: 'smooth',
          });
        } else {
          // For other tabs, scroll to their section
          const element = document.getElementById(tab);
          if (element) {
            scrollRef.current.scrollTo({
              top: element.offsetTop,
              behavior: 'smooth',
            });
          }
        }
      }
    },
    [highlightSection]
  );

  const Tab = ({ target, children }) => (
    <span
      className={classNames(styles.tab, {
        [styles.active]: activeTab === target,
      })}
      onClick={(e) => handleTabClick(target, e)}
    >
      {children}
    </span>
  );

  return (
    <div className={styles.preview}>
      <div className={styles.header}>
        <div className={styles.title}>
          <span>
            <strong>{_.get(data, 'order')}:</strong>{' '}
            {_.get(data, [
              'name',
              _.findKey(_.get(data, ['languages']), (item) => item === 1),
            ])}
          </span>
          <CloseBigIcon
            onClick={() =>
              actions.setTableParams({
                clicked_row: undefined,
              })
            }
          />
        </div>
        <div className={styles.actions}>
          <div className={styles.actionWrapper}>
            <Tooltip text={__('projects.tooltips.stages.move-up')}>
              <MoveRowUpIcon
                onClick={() => {
                  updateStageOrder(data.id, 'up').then(() => {
                    listProjectStages(match.params.project);
                    readStage(data.id);
                    setNotification(
                      __('project.stages.notification.order-successfully-set')
                    );
                    listActiveModules();
                  });
                }}
              />
            </Tooltip>
          </div>
          <div className={styles.actionWrapper}>
            <Tooltip text={__('projects.tooltips.stages.move-down')}>
              <MoveRowDownIcon
                onClick={() => {
                  updateStageOrder(data.id, 'down').then(() => {
                    listProjectStages(match.params.project);
                    readStage(data.id);
                    setNotification(
                      __('project.stages.notification.order-successfully-set')
                    );
                    listActiveModules();
                  });
                }}
              />
            </Tooltip>
          </div>
        </div>
      </div>

      <div className={styles.tabsWrapper}>
        <ArrowDownMiddleIcon
          className={classNames(
            styles.collapseAll,
            _.size(collapsedTabs) === _.size(tabs) && styles.collapsed
          )}
          onClick={toggleCollapseTabs}
        />
        <div className={styles.tabs}>
          <Tab target='properties'>{__('stage.flyout.properties')}</Tab>
          <Tab target='substages'>
            {__('stage.flyout.substages')}{' '}
            {!_.isEmpty(data.substages) && ' (' + _.size(data.substages) + ')'}
          </Tab>
        </div>
      </div>
      <div className={styles.content} ref={scrollRef}>
        <Element
          name='properties'
          id='properties'
          className={classNames(
            styles.section,
            highlightedTab === 'properties' && styles.highlighted
          )}
        >
          <div
            className={styles.title}
            onClick={() => toggleCollapseTab('properties')}
          >
            <ArrowDownMiddleIcon
              className={classNames(
                styles.collapse,
                _.includes(collapsedTabs, 'properties') && styles.collapsed
              )}
            />
            {__('stage.flyout.properties')}
          </div>
          <Properties
            collapsed={_.includes(collapsedTabs, 'properties')}
            data={data}
            readAction={readAction}
            is_project_administrator={is_project_administrator}
          />
        </Element>
        <Element
          name='substages'
          id='substages'
          className={classNames(
            styles.section,
            highlightedTab === 'substages' && styles.highlighted
          )}
        >
          <div
            className={styles.title}
            onClick={() => toggleCollapseTab('substages')}
          >
            <ArrowDownMiddleIcon
              className={classNames(
                styles.collapse,
                _.includes(collapsedTabs, 'substages') && styles.collapsed
              )}
            />
            {__('stage.flyout.substages')}
          </div>
          <Substages
            collapsed={_.includes(collapsedTabs, 'substages')}
            data={data}
            readAction={readAction}
            is_project_administrator={is_project_administrator}
          />
        </Element>
      </div>
    </div>
  );
};

export default connect(mapStateToProps, mapDispatchToProps)(StagesFlyout);
